import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Page, Run, Stats } from "../lib/types";
import { getPages, getRuns, getStats, toMessage, triggerPoll } from "../lib/api";
import { relativeTime } from "../lib/format";
import { SOCKET_EVENTS, useSocketEvent } from "../lib/socket";
import { useAuth } from "../lib/auth";
import RunTable from "../components/RunTable";
import NavGraph from "../components/NavGraph";
import OperatorControls from "../components/OperatorControls";

/**
 * The operator view: what the watcher knows, how it learned it, and whether the
 * last poll was cheap. Everyone can read it; only an admin can start a poll.
 */

const STYLE_ID = "aw-admin-styles";

const CSS = `
.aw-ad {
  --ink: #0D0D0D;
  --red: #FF0000;
  --pink: #E89AC7;
  --tag: #FFFFFF;
  --rule: rgba(13, 13, 13, 0.14);
  --mute: #6B6B6B;
  --anton: "Anton", "Oswald", Impact, Haettenschweiler, sans-serif;
  --oswald: "Oswald", "Anton", "Arial Narrow", Arial, sans-serif;
  --sora: "Sora", "Helvetica Neue", Helvetica, Arial, sans-serif;
  box-sizing: border-box;
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: clamp(56px, 9vw, 88px) clamp(16px, 4vw, 40px) clamp(64px, 10vw, 112px);
  color: var(--ink);
}
.aw-ad *, .aw-ad *::before, .aw-ad *::after { box-sizing: border-box; }

.aw-ad-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 18px;
  padding-bottom: 18px;
  border-bottom: 2px solid var(--ink);
}
.aw-ad-eyebrow {
  margin: 0 0 6px;
  font-family: var(--sora);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: var(--mute);
}
.aw-ad-title {
  margin: 0;
  font-family: var(--anton);
  font-size: clamp(34px, 8vw, 62px);
  font-weight: 400;
  line-height: 0.9;
  letter-spacing: 0.01em;
  text-transform: uppercase;
}
.aw-ad-last {
  margin: 8px 0 0;
  font-family: var(--sora);
  font-size: 11px;
  letter-spacing: 0.06em;
  color: var(--mute);
}
.aw-ad-actions { display: flex; flex-direction: column; align-items: flex-start; gap: 8px; }

.aw-ad-poll {
  font-family: var(--anton);
  font-size: 16px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--tag);
  background: var(--ink);
  border: 2px solid var(--ink);
  padding: 12px 24px 10px;
  cursor: pointer;
  transition: background 120ms linear, color 120ms linear;
}
.aw-ad-poll:hover:enabled { background: var(--pink); color: var(--ink); }
.aw-ad-poll:disabled { background: transparent; color: var(--mute); border-color: var(--rule); cursor: not-allowed; }
.aw-ad-poll:focus-visible { outline: 2px solid var(--pink); outline-offset: 3px; }

.aw-ad-note {
  margin: 0;
  font-family: var(--sora);
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--mute);
}
.aw-ad-pollerr {
  margin: 0;
  font-family: var(--sora);
  font-size: 11px;
  color: var(--red);
  max-width: 34ch;
}

.aw-ad-warn {
  margin: 18px 0 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  border-left: 3px solid var(--red);
  padding: 8px 12px;
  font-family: var(--sora);
  font-size: 11px;
  letter-spacing: 0.06em;
  color: var(--red);
}

/* Rules live on the tiles, not in the grid gap, so a row that does not divide
   evenly leaves a clean ragged edge instead of a block of exposed background. */
.aw-ad-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
  margin-top: 26px;
  border-top: 1px solid var(--ink);
  border-left: 1px solid var(--ink);
}
.aw-ad-tile {
  background: var(--tag);
  padding: 16px 14px 14px;
  min-width: 0;
  border-right: 1px solid var(--ink);
  border-bottom: 1px solid var(--ink);
}
.aw-ad-tile-label {
  display: block;
  font-family: var(--sora);
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--mute);
}
.aw-ad-tile-value {
  display: block;
  margin-top: 8px;
  font-family: var(--anton);
  font-size: clamp(26px, 4.6vw, 40px);
  line-height: 0.95;
  font-variant-numeric: tabular-nums;
}
.aw-ad-tile.is-red .aw-ad-tile-value { color: var(--red); }
.aw-ad-tile.is-accent { box-shadow: inset 0 -5px 0 var(--pink); }

/* A run in flight. The bar is indeterminate on purpose: the backend reports a
   run's counters when it starts and when it ends, never in between. */
.aw-ad-live {
  margin-top: 26px;
  border: 1px solid var(--ink);
  background: var(--tag);
  overflow: hidden;
}
.aw-ad-live-body {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px 14px;
  padding: 14px 16px;
}
.aw-ad-live-tag {
  font-family: var(--anton);
  font-size: 15px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.aw-ad-live-meta {
  font-family: var(--sora);
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--mute);
}
.aw-ad-live-clock {
  font-family: var(--oswald);
  font-size: 15px;
  font-variant-numeric: tabular-nums;
  margin-left: auto;
}
.aw-ad-live-bar { height: 4px; background: rgba(232, 154, 199, 0.3); overflow: hidden; }
.aw-ad-live-bar i {
  display: block;
  width: 34%;
  height: 100%;
  background: var(--pink);
  animation: aw-ad-sweep 1.4s ease-in-out infinite;
}
@keyframes aw-ad-sweep {
  0% { transform: translateX(-110%); }
  100% { transform: translateX(320%); }
}
@media (prefers-reduced-motion: reduce) {
  .aw-ad-live-bar i { animation: none; width: 100%; opacity: 0.6; }
}

.aw-ad-section { margin-top: 44px; }
.aw-ad-section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--ink);
}
.aw-ad-h2 {
  margin: 0;
  font-family: var(--anton);
  font-size: clamp(20px, 3.6vw, 28px);
  font-weight: 400;
  line-height: 1;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.aw-ad-h2-sub {
  font-family: var(--sora);
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--mute);
}

.aw-ad-state {
  margin: 40px 0 0;
  border: 1px solid var(--ink);
  background: var(--tag);
  padding: 40px 20px;
  text-align: center;
}
.aw-ad-state-title {
  margin: 0;
  font-family: var(--anton);
  font-size: 22px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.aw-ad-state-title.is-error { color: var(--red); }
.aw-ad-state-msg {
  margin: 10px auto 0;
  max-width: 52ch;
  font-family: var(--sora);
  font-size: 12px;
  line-height: 1.55;
  color: var(--mute);
  overflow-wrap: anywhere;
}
.aw-ad-retry {
  margin-top: 18px;
  font-family: var(--oswald);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  background: transparent;
  color: var(--ink);
  border: 1px solid var(--ink);
  padding: 8px 18px;
  cursor: pointer;
}
.aw-ad-retry:hover { background: var(--ink); color: var(--tag); }

@media (max-width: 640px) {
  .aw-ad-head { align-items: flex-start; }
  .aw-ad-actions { width: 100%; }
  .aw-ad-poll { width: 100%; }
  .aw-ad-live-clock { margin-left: 0; }
}
`;

if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

const RUN_LIMIT = 25;
/** A run that has been "in flight" this long lost its finish event; unlock the control. */
const STALE_RUN_MS = 10 * 60 * 1000;

const stopwatch = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const tileValue = (n?: number): string =>
  typeof n === "number" && Number.isFinite(n) ? n.toLocaleString() : "—";

export function Admin() {
  const { isAdmin } = useAuth();

  const [stats, setStats] = useState<Stats | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [failedSources, setFailedSources] = useState<string[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [activeRun, setActiveRun] = useState<Run | null>(null);
  const [requestingPoll, setRequestingPoll] = useState(false);
  const [pollError, setPollError] = useState<string | null>(null);
  const [tick, setTick] = useState(() => Date.now());

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const mergeRun = useCallback((run: Run) => {
    setRuns((prev) => {
      const index = prev.findIndex((existing) => existing._id === run._id);
      if (index === -1) return [run, ...prev].slice(0, RUN_LIMIT);
      const next = prev.slice();
      next[index] = run;
      return next;
    });
  }, []);

  // Settled rather than all: one dead endpoint should not blank the other two.
  const load = useCallback(async (signal?: AbortSignal, showSpinner = true) => {
    if (showSpinner) setLoading(true);

    const [statsResult, runsResult, pagesResult] = await Promise.allSettled([
      getStats(signal),
      getRuns(RUN_LIMIT, signal),
      getPages(signal),
    ]);

    if (signal?.aborted || !mounted.current) return;

    const missing: string[] = [];

    if (statsResult.status === "fulfilled") setStats(statsResult.value);
    else missing.push("stats");

    if (runsResult.status === "fulfilled") {
      const nextRuns = Array.isArray(runsResult.value) ? runsResult.value : [];
      setRuns(nextRuns);
      setActiveRun((current) => {
        const running = nextRuns.find((run) => run.status === "running");
        if (running) return running;
        // A run the socket told us about can be newer than this list, so it is
        // only dropped once the list actually shows it — otherwise a refetch
        // racing "ap:run-started" would blank the live bar it just lit.
        return current && !nextRuns.some((run) => run._id === current._id) ? current : null;
      });
    } else {
      missing.push("runs");
    }

    if (pagesResult.status === "fulfilled") {
      setPages(Array.isArray(pagesResult.value) ? pagesResult.value : []);
    } else {
      missing.push("pages");
    }

    setFailedSources(missing);
    // Only a total failure is worth an error screen; a partial one degrades in place.
    setLoadError(
      missing.length === 3 && statsResult.status === "rejected"
        ? toMessage(statsResult.reason)
        : null
    );
    setLoading(false);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // The socket is the only way this screen learns a poll began somewhere else —
  // a cron tick, or another operator.
  useSocketEvent<Run>(SOCKET_EVENTS.runStarted, (run) => {
    setActiveRun(run);
    mergeRun(run);
  });

  useSocketEvent<Run>(SOCKET_EVENTS.runFinished, (run) => {
    mergeRun(run);
    setActiveRun((current) => (current && current._id !== run._id ? current : null));
    // A finished run changes the counts and may have revealed a new page.
    void load(undefined, false);
  });

  useEffect(() => {
    if (!activeRun) return;
    const id = window.setInterval(() => setTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [activeRun]);

  const activeElapsed = activeRun ? tick - Date.parse(activeRun.startedAt) : 0;
  const runIsStale = activeRun !== null && activeElapsed > STALE_RUN_MS;
  const pollDisabled = requestingPoll || (activeRun !== null && !runIsStale);

  const startPoll = useCallback(async () => {
    setRequestingPoll(true);
    setPollError(null);
    try {
      // 202 and nothing else: the Run itself reaches us on "ap:run-started".
      // The refetch is the fallback for a client whose socket never connected —
      // it picks the running row out of /runs the same way the first load does.
      await triggerPoll("manual");
      if (!mounted.current) return;
      setTick(Date.now());
      await load(undefined, false);
    } catch (err) {
      if (mounted.current) setPollError(toMessage(err));
    } finally {
      if (mounted.current) setRequestingPoll(false);
    }
  }, [load]);

  const lastFinished = useMemo(() => runs.find((run) => run.status !== "running"), [runs]);

  const tiles = useMemo(
    () => [
      { key: "products", label: "Products", value: stats?.products, tone: "" },
      { key: "live", label: "Live", value: stats?.live, tone: "" },
      { key: "soldOut", label: "Sold out", value: stats?.soldOut, tone: "is-red" },
      { key: "unlisted", label: "Unlisted", value: stats?.unlisted, tone: "is-accent" },
      { key: "pages", label: "Pages", value: stats?.pages, tone: "" },
      { key: "orphanPages", label: "Orphan pages", value: stats?.orphanPages, tone: "is-accent" },
      { key: "events24h", label: "Events 24h", value: stats?.events24h, tone: "" },
    ],
    [stats]
  );

  if (loading && !stats && runs.length === 0 && pages.length === 0) {
    return (
      <main className="aw-ad">
        <p className="aw-ad-eyebrow">Anti Watcher</p>
        <h1 className="aw-ad-title">Operations</h1>
        <div className="aw-ad-state">
          <p className="aw-ad-state-title">Reading the log</p>
          <p className="aw-ad-state-msg">Fetching stats, poll history and the crawled site map.</p>
        </div>
      </main>
    );
  }

  if (loadError) {
    return (
      <main className="aw-ad">
        <p className="aw-ad-eyebrow">Anti Watcher</p>
        <h1 className="aw-ad-title">Operations</h1>
        <div className="aw-ad-state">
          <p className="aw-ad-state-title is-error">Nothing answered</p>
          <p className="aw-ad-state-msg">{loadError}</p>
          <button type="button" className="aw-ad-retry" onClick={() => void load()}>
            Try again
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="aw-ad">
      <header className="aw-ad-head">
        <div>
          <p className="aw-ad-eyebrow">Anti Watcher</p>
          <h1 className="aw-ad-title">Operations</h1>
          <p className="aw-ad-last">
            {lastFinished
              ? `Last poll ${relativeTime(lastFinished.startedAt)} · ${lastFinished.mode} · ${lastFinished.status}`
              : "No completed poll on record"}
          </p>
        </div>

        <div className="aw-ad-actions">
          {isAdmin ? (
            <>
              <button
                type="button"
                className="aw-ad-poll"
                onClick={() => void startPoll()}
                disabled={pollDisabled}
              >
                {pollDisabled ? "Polling…" : "Poll now"}
              </button>
              {pollError ? <p className="aw-ad-pollerr">{pollError}</p> : null}
            </>
          ) : (
            <p className="aw-ad-note">Read only — starting a poll is an admin action</p>
          )}
        </div>
      </header>

      {failedSources.length > 0 ? (
        <p className="aw-ad-warn">
          <span>Could not load: {failedSources.join(", ")}</span>
          <button type="button" className="aw-ad-retry" onClick={() => void load()}>
            Retry
          </button>
        </p>
      ) : null}

      <section className="aw-ad-tiles" aria-label="Watcher totals">
        {tiles.map((tile) => (
          <div key={tile.key} className={`aw-ad-tile ${tile.tone}`}>
            <span className="aw-ad-tile-label">{tile.label}</span>
            <span className="aw-ad-tile-value">{tileValue(tile.value)}</span>
          </div>
        ))}
      </section>

      {activeRun ? (
        <div className="aw-ad-live" role="status" aria-live="polite">
          <div className="aw-ad-live-body">
            <span className="aw-ad-live-tag">{activeRun.mode} poll running</span>
            <span className="aw-ad-live-meta">
              {activeRun.pagesCrawled} pages · {activeRun.productsSeen} products ·{" "}
              {activeRun.eventsCreated} events
            </span>
            <span className="aw-ad-live-clock">{stopwatch(activeElapsed)}</span>
          </div>
          <span className="aw-ad-live-bar" aria-hidden="true">
            <i />
          </span>
        </div>
      ) : null}

      <OperatorControls />

      <section className="aw-ad-section">
        <div className="aw-ad-section-head">
          <h2 className="aw-ad-h2">Poll log</h2>
          <span className="aw-ad-h2-sub">
            304 cache ratio is how we know conditional requests are working
          </span>
        </div>
        <RunTable runs={runs} />
      </section>

      <section className="aw-ad-section">
        <div className="aw-ad-section-head">
          <h2 className="aw-ad-h2">Site map</h2>
          <span className="aw-ad-h2-sub">
            {pages.length} {pages.length === 1 ? "page" : "pages"} known
          </span>
        </div>
        <NavGraph pages={pages} />
      </section>
    </main>
  );
}

export default Admin;
